import apiClient from "@/api/client";
import type {
  FetchAddressesResult,
  NormChange,
  NormRow,
  RouteImportPayload,
  RouteImportResult,
  RouteImportSource,
  TechCalcDictionaries,
  TechCalcDrawing,
  TechCalcProject,
  TechCalcProjectDetail,
  TechCalcVariant,
  VariantResults,
} from "./types";

// DLC_10 routes live under /api/v1/tech-calc/ (apiClient already carries /api/v1).
const BASE = "/tech-calc";

// Every endpoint answers with the { data, errors, meta } envelope — unwrap `data`.
async function unwrap<T>(p: Promise<{ data: { data: T } }>): Promise<T> {
  const r = await p;
  return r.data.data;
}

// ── projects ─────────────────────────────────────────────────────────────────
export const getProjects = () =>
  unwrap<TechCalcProject[]>(apiClient.get(`${BASE}/projects/`));

export const getProject = (id: string) =>
  unwrap<TechCalcProjectDetail>(apiClient.get(`${BASE}/projects/${id}/`));

export const createProject = (payload: { name: string; description: string }) =>
  unwrap<TechCalcProjectDetail>(apiClient.post(`${BASE}/projects/`, payload));

export const patchProject = (id: string, payload: Partial<{ name: string; description: string }>) =>
  unwrap<TechCalcProjectDetail>(apiClient.patch(`${BASE}/projects/${id}/`, payload));

export const deleteProject = (id: string) => apiClient.delete(`${BASE}/projects/${id}/`);

// ── variants ─────────────────────────────────────────────────────────────────
export const getVariants = () =>
  unwrap<TechCalcVariant[]>(apiClient.get(`${BASE}/variants/`));

export const createVariant = (project: string, name: string) =>
  unwrap<TechCalcVariant>(apiClient.post(`${BASE}/variants/`, { project, name }));

/** Dokładna kopia wariantu (rysunek + ustawienia + zapisane wyniki) w tym samym
 *  projekcie. Bez nazwy backend nada "<nazwa> (kopia)". */
export const duplicateVariant = (id: string, name?: string) =>
  unwrap<TechCalcVariant>(
    apiClient.post(`${BASE}/variants/${id}/duplicate/`, name ? { name } : {}),
  );

export const patchVariant = (
  id: string,
  payload: Partial<Pick<TechCalcVariant, "name" | "access_lines_coeff">>,
) => unwrap<TechCalcVariant>(apiClient.patch(`${BASE}/variants/${id}/`, payload));

// ── import trasy z projektu ZE (DLC_01) ──────────────────────────────────────
// Oba endpointy wymagają dostępu i do DLC_10, i do DLC_01 — bez tego drugiego
// odpowiadają 403 (dlatego UI pokazuje opcję tylko z modułem dlc_01).
export const getRouteSources = () =>
  unwrap<RouteImportSource[]>(apiClient.get(`${BASE}/route-sources/`));

/** Nowy wariant obliczeń z trasy projektu ZE. Poza samym wariantem zwraca
 *  podsumowanie z `meta.summary`: ile słupów/przęseł powstało i co pominięto. */
export const importRouteVariant = async (
  projectId: string,
  payload: RouteImportPayload,
): Promise<RouteImportResult> => {
  const r = await apiClient.post(`${BASE}/projects/${projectId}/import-route/`, payload);
  return { variant: r.data.data, summary: r.data.meta.summary };
};

// ── drawing ──────────────────────────────────────────────────────────────────
export const getDrawing = (variantId: string) =>
  unwrap<TechCalcDrawing>(apiClient.get(`${BASE}/variants/${variantId}/drawing/`));

/** Full replace of the variant drawing; the backend clears computed results. */
export const saveDrawing = (variantId: string, fc: TechCalcDrawing) =>
  unwrap<unknown>(apiClient.post(`${BASE}/variants/${variantId}/save-drawing/`, fc));

/** Reverse-geocode drawn points via OSM (server-side). Only unstamped points are
 *  queried; force=true re-queries all. */
export const fetchAddresses = (variantId: string, force = false) =>
  unwrap<FetchAddressesResult>(
    apiClient.post(
      `${BASE}/variants/${variantId}/fetch-addresses/`,
      force ? { force: true } : {},
    ),
  );

// ── computation ──────────────────────────────────────────────────────────────
export const recompute = (variantId: string) =>
  unwrap<VariantResults>(apiClient.post(`${BASE}/variants/${variantId}/recompute/`, {}));

export const getResults = (variantId: string) =>
  unwrap<VariantResults>(apiClient.get(`${BASE}/variants/${variantId}/results/`));

// ── dictionaries ─────────────────────────────────────────────────────────────
export const getDictionaries = () =>
  unwrap<TechCalcDictionaries>(apiClient.get(`${BASE}/dictionaries/`));

// ── norm catalog ─────────────────────────────────────────────────────────────
export type NormKind = "lines" | "poles";

export const getNorms = (kind: NormKind) =>
  unwrap<NormRow[]>(apiClient.get(`${BASE}/norms/${kind}/`));

export const createNorm = (kind: NormKind, payload: Record<string, unknown>) =>
  unwrap<NormRow>(apiClient.post(`${BASE}/norms/${kind}/`, payload));

export const patchNorm = (kind: NormKind, id: string, payload: Record<string, unknown>) =>
  unwrap<NormRow>(apiClient.patch(`${BASE}/norms/${kind}/${id}/`, payload));

export const deleteNorm = (kind: NormKind, id: string) =>
  apiClient.delete(`${BASE}/norms/${kind}/${id}/`);

export const getNormChanges = () =>
  unwrap<NormChange[]>(apiClient.get(`${BASE}/norms/changes/`));

// ── XLSX export ──────────────────────────────────────────────────────────────
// Fetched as a blob through the authenticated client so the JWT header is sent
// (a plain <a href> can't carry it).
export const downloadVariantXlsx = async (variantId: string): Promise<Blob> => {
  const r = await apiClient.get(`${BASE}/variants/${variantId}/export/`, {
    responseType: "blob",
  });
  return r.data as Blob;
};

// ── PDF cards export ─────────────────────────────────────────────────────────
// Karty renderuje frontend (markup + CSS aplikacji), backend drukuje je do PDF
// w headlessowym Chromium: cards-pdf odsyła ZIP wszystkich kart, card-pdf —
// pojedynczy PDF (przycisk „Drukuj" na stronie karty). Payload jedzie jako
// plik multipart, żeby CSS (setki KB) nie wpadł w limit
// DATA_UPLOAD_MAX_MEMORY_SIZE.
export interface CardPdfPayload {
  css: string;
  cards: { name: string; title: string; markup: string }[];
}

const postCardsPayload = async (url: string, payload: CardPdfPayload): Promise<Blob> => {
  const fd = new FormData();
  fd.append(
    "payload",
    new Blob([JSON.stringify(payload)], { type: "application/json" }),
    "payload.json",
  );
  try {
    const r = await apiClient.post(url, fd, {
      responseType: "blob",
      // apiClient defaults to application/json — z tym nagłówkiem axios 1.x
      // SERIALIZUJE FormData do JSON-a (plik znika: {"payload": {}}); override
      // przywraca prawdziwy multipart z boundary od przeglądarki.
      headers: { "Content-Type": "multipart/form-data" },
    });
    return r.data as Blob;
  } catch (err) {
    // responseType:"blob" → błędna odpowiedź też przychodzi jako Blob; rozpakuj
    // kopertę JSON, żeby extractApiError pokazał właściwy komunikat.
    const resp = (err as { response?: { data?: unknown } }).response;
    if (resp && resp.data instanceof Blob) {
      try {
        resp.data = JSON.parse(await resp.data.text());
      } catch {
        // not JSON — keep the original error
      }
    }
    throw err;
  }
};

export const renderCardsPdfZip = (variantId: string, payload: CardPdfPayload): Promise<Blob> =>
  postCardsPayload(`${BASE}/variants/${variantId}/cards-pdf/`, payload);

export const renderCardPdf = (variantId: string, payload: CardPdfPayload): Promise<Blob> =>
  postCardsPayload(`${BASE}/variants/${variantId}/card-pdf/`, payload);
